/**
 * Where a listing's description text lives, and what a save writes.
 *
 * The description is moving out of the `description` attribute and into
 * `publicData.description`. Sharetribe matches a keyword search against the
 * title and the description attribute by default, and against extended data
 * only where a text search schema is defined — so a dealer's 4000-character
 * description naming every brand on the forecourt matches practically every
 * search, which is what makes the result page look unrelated to what was
 * typed. In publicData, with no schema on that key, it drops out of the index.
 *
 * The move is not instantaneous: the API's migration walks the existing
 * listings, and until every one has been through it both places hold
 * descriptions. Every reader therefore has to consult both.
 *
 * publicData is not simply preferred over the attribute, because it can be
 * stale. Sharetribe rejects a blank `description` ("must be between 1 and 5000
 * characters"), so nothing can empty the attribute — the migration leaves the
 * title in it instead. A description edited through a client that still writes
 * the attribute lands there and nowhere else, which makes the attribute the
 * newer of the two. So the attribute wins whenever it holds something the
 * migration would not have put there.
 *
 * Kept in sync with resolveDescription in the API
 * (src/entities/listings/utils/listing-description.util.ts) and with its
 * server-side twin in server/api-util/listingDescription.js.
 */

/**
 * What the migration leaves in the attribute, which cannot be blank. A draft
 * can be titleless, hence the '-'.
 *
 * @param {string|undefined} title the listing's title
 * @returns {string} the leftover to store in the `description` attribute
 */
export const descriptionLeftover = title => title?.trim() || '-';

/**
 * The listing's description text, from whichever place currently holds it.
 *
 * @param {Object} listing a listing entity, or anything with `attributes`
 * @returns {string|undefined} the text, or undefined when there is none
 */
export const resolveDescription = listing => {
  const attributes = listing?.attributes;
  const current = attributes?.description?.trim();

  if (current && current !== descriptionLeftover(attributes?.title)) {
    return current;
  }

  return attributes?.publicData?.description?.trim() || current || undefined;
};

/**
 * What a save stores in `publicData.description`.
 *
 * `null` for an empty description, and never `undefined`. Not because the two
 * differ — the SDK serializes them identically, and a null publicData key is
 * how Sharetribe is told to *remove* it — but because they read as though they
 * did. `undefined` looks like "skip this field" and is nothing of the kind,
 * which is exactly how the feed importer came to erase live descriptions. So
 * the removal is spelled out where a reader can see it.
 *
 * A save writes this *and* the leftover into the attribute, in the same update.
 * Writing publicData alone would leave a listing that has not been migrated yet
 * holding the old text in its attribute — and since the attribute wins while it
 * holds something other than the title, resolveDescription would then serve the
 * stale version. Sending both keeps them consistent, which also means an
 * ordinary save migrates that listing on its own.
 *
 * @param {string|undefined} description the text the seller entered
 * @returns {string|null} the value to store, or null to remove the key
 */
export const descriptionForPublicData = description => description?.trim() || null;
